import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetOrgsQuery, useGetCurrentUserDetailQuery, useGetUsersMutation, useGetRolesMutation, useGetAllPermissionSetQuery, useGetRolesForFilterMutation } from "../api/userSlice";
import {
	useGetVehiclesQuery,
	useGetSuppliersMutation,
	useGetProductsMutation,
	useGetProductCategoriesMutation,
	useGetCheckPointQuery,
	useGetTemplateDetailQuery,
	useLazyGetCheckpointDataListQuery,
	useGetDashboardStatsMutation,
	useGetCheckpointTypeMutation,
	useGetDynamicModuleMutation,
	useGetDynamicModuleEntryMutation,
	useGetTemplatesMutation,
	useGetCheckPointOnCallMutation,
	useGetWorkflowListingQuery,
	useGetTemplateDetailOnCallMutation,
	useGetRuleDetailMutation,
	useGetThirdPartyLogsMutation,
	useGetUserTableHeadersMutation,
	useGetPlantMutation,
	useGetWorkflowListMutation,
	useGetStateOfficesMutation,
	useGetTripDetailMutation,
	useGetResourceMutation,
	useFactoryDashboardDataMutation,
	useGetOrgInsightsMutation,
	useViewPlantCommentMutation,
	useGetCheckpointDataListForExcelDownloadMutation,
} from "../api/vmsSlice";
import { useGetAssetTagMutation, useGetDeviceConfigMutation, useGetDeviceLogsMutation, useGetGraphTimelineDataMutation, useGetTagsMutation, useGetTcpClientsMutation, useGetTelemetryDataMutation } from "../api/deviceSlice";
import { useGetIdleWarehousesMutation } from "../api/api";
import { socket } from "../socket";
import { toastMessage, dismissToast } from "../Components/utils";
import { setFavicon, setTitle, customAPI, saveAsExcel, debounce } from "./functions";
import { useBranding } from "../context/brandingContext";
import * as serviceWorkerRegistration from "../serviceWorkerRegistration";
import { toast } from "react-toastify";

import { useDeviceContext, useSocketContext } from "../context/socketContext";
import { usePlantContext } from "../context/plantContext";

//device
function useDevices(filter) {
	const [getDevices, { data, isLoading }] = useGetDeviceConfigMutation({ fixedCacheKey: "getDevices" });

	useEffect(() => {
		if (filter) getDevices(filter);
	}, []);

	let { data: devices, count, message, error } = data || { data: [] };

	if (!devices) devices = [];
	return { devices, count, message, isLoading, getDevices };
}
function useDeviceLogs(filter) {
	const [getDeviceLogs, { data, isLoading }] = useGetDeviceLogsMutation({ fixedCacheKey: "getDevicesLogs" });

	useEffect(() => {
		if (filter) getDeviceLogs(filter);
	}, []);

	let { data: logs, count, message, error } = data || { data: [] };

	if (!logs) logs = [];
	return { logs, count, message, isLoading, getDeviceLogs };
}
function useTags(filter, cacheKey = "getTags") {
	const [getTags, { data, isLoading }] = useGetTagsMutation({ fixedCacheKey: cacheKey });
	useEffect(() => {
		if (filter) getTags(filter);
	}, []);

	let { data: tags, count, message } = data || { data: [] };
	if (!tags) tags = [];
	const tagOptions = tags.map((_) => ({ label: _.displayName, value: _._id, data: _ }));
	return { tags, count, message, isLoading, getTags, tagOptions };
}
function useTagsForScan(filter) {
	const [fetchData, { data: rtkData, isLoading, reset }] = useGetTagsMutation({ fixedCacheKey: "getTagsForScan" });
	useEffect(() => {
		if (filter) fetchData(filter);
	}, []);

	let { data: scannedTags, count, message } = rtkData || { data: [] };
	if (!scannedTags) scannedTags = [];
	return {
		scannedTags,
		count,
		message,
		isLoading,
		getTagsScan: fetchData,
		clearData: () => {
			reset();
		},
	};
}
function useAssetTags(filter) {
	const [fetchData, { data: rtkData, isLoading, reset }] = useGetAssetTagMutation({ fixedCacheKey: "getAssetTags" });
	useEffect(() => {
		if (filter) fetchData(filter);
	}, []);

	let { data: assetTags, count, message } = rtkData || { data: [] };
	if (!assetTags) assetTags = [];
	return {
		assetTags,
		count,
		message,
		isLoading,
		getAssetTags: fetchData,
		clearData: () => {
			reset();
		},
	};
}
function useTelemetryData(filter) {
	const [getTelemetryData, { data, isLoading }] = useGetTelemetryDataMutation({});
	useEffect(() => {
		if (filter) getTelemetryData(filter);
	}, []);

	let { data: telemetryData, count, message } = data || { data: [] };
	if (!telemetryData) telemetryData = [];
	return { telemetryData, count, message, isLoading, getTelemetryData };
}
//user
function useUsers(filter) {
	const [getUsers, { data: _data, isLoading }] = useGetUsersMutation({ fixedCacheKey: "getUsers" });
	useEffect(() => {
		if (filter) getUsers(filter);
	}, []);

	let { data: users, count, message, error } = _data || { data: [] };
	if (!users) users = [];
	const userOptions = users.map((_) => ({ label: _.name, value: _._id })); //[{label: "--select--", value: ""}]

	return { getUsers, users, count, message, isLoading, userOptions };
}
function useOrgs(filter = {}, { skip = false } = {}) {
	const stableFilter = useMemo(() => filter, [JSON.stringify(filter)]);

	const { data, refetch, isLoading, isFetching } = useGetOrgsQuery(stableFilter, {
		refetchOnMountOrArgChange: !skip,
		skip,
	});

	let { data: orgs, count, message, error } = data || { data: [] };
	if (!orgs) orgs = [];
	const orgOptions = [
		// { label: "--select--", value: "" },
		...(orgs?.map((org) => {
			return { label: org.name, value: org._id, data: org };
		}) || []),
	];
	return { orgs, count, message, isLoading: isFetching, refetch, orgOptions };
}
function useWorkflow(filter = {}) {
	const [getWorkflow, { data, isLoading }] = useGetWorkflowListMutation(filter, { refetchOnMountOrArgChange: true });
	useEffect(() => {
		if (filter) getWorkflow(filter);
	}, []);
	let { data: workflowListing = [], count, message, error } = data || { data: [] };
	const workflowOptions = [...(workflowListing?.map((_) => ({ label: _.name, value: _._id })) || [])];
	return { workflowListing, workflowOptions, message, error, isLoading, count, getWorkflow };
}
function useAllPermissionSet() {
	const { data, refetch, isFetching } = useGetAllPermissionSetQuery(
		{ project: "lynkid" },
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const { data: permData, message, error } = data || { data: [] };

	return {
		permissions: permData?.reduce((res, curr) => {
			res[curr.module] = curr.permissions || {};
			return res;
		}, {}),
		refetch,
		isLoading: isFetching,
	};
}

// FUNCTION FOR REMOVING THE PREFIX PLANT-PLANTID FROM THE USERDETAIL PERMISSION
function filterPermissionsByPlantId(userPermissions, plantId) {
	// console.log("plantId recevied in the hook", plantId)
	if (!plantId || !userPermissions) return userPermissions || {};

	const prefix = `plant-${plantId}`;
	const result = {};

	for (const [key, value] of Object.entries(userPermissions)) {
		if (key.startsWith(`${prefix}-`)) {
			result[key.substring(prefix.length + 1)] = value;
		} else {
			result[key] = value;
		}
	}
	return result;
}

function useUserPermissions() {
	const { plantId } = usePlantContext();
	const { userDetail, isUserDetailSuccess } = useUserDetail();

	let { userPermissions, apiPermissions } = useMemo(() => {
		return (
			userDetail?.userPermissions?.reduce(
				(acc, curr) => {
					const targetKey = curr.module === "api" ? "apiPermissions" : "userPermissions";
					return {
						...acc,
						[targetKey]: {
							...acc[targetKey],
							...curr.permissions,
						},
					};
				},
				{ userPermissions: {}, apiPermissions: {} }
			) || { userPermissions: {}, apiPermissions: {} }
		);
	}, [userDetail]);


	const filteredPermissions = useMemo(() => {
		return filterPermissionsByPlantId(userPermissions, plantId);
	}, [userPermissions, plantId]);

	userPermissions = filteredPermissions;
	// console.log("UserPermissions", userPermissions)
	userPermissions && Object.keys(userPermissions).length && userPermissions.offlineAccess?.value ? localStorage.setItem("offlinePermission", true) : localStorage.setItem("offlinePermission", false);
	return {
		userPermissions,
		apiPermissions,
		isFetched: isUserDetailSuccess,
		isRespose: Object.keys(userPermissions).length,
	};
}

function useAuth() {
	const { userDetail, statusCode, isUserDetailSuccess } = useUserDetail();
	const { userPermissions, isRespose, isFetched } = useUserPermissions();
	const { checkpoints, isSuccess, isCheckpointFetched } = useCheckpoints();

	if (isUserDetailSuccess) {
		if (statusCode == 401) return "/not-authorized";
		if (userDetail?.privacyPolicyAcknowledged === false) return "/welcome";
		const { userType } = userDetail || {};
		if (userType == "superAdmin") return "/dashboard";

		if (isFetched) {
			if (!isRespose || !Object.keys(userPermissions).length) return "/not-authorized";

			if (userPermissions.getDashboard?.value) {
				return "/dashboard";
			} else if (userPermissions.getDashboardV2?.value) {
				return "/dashboard";
			} else if (userPermissions?.viewVehicleMovementStats?.value) {
				return "/vehicle-movement-stats";
			}

			const checkpointPerm = Object.keys(userPermissions).find((key) => key.includes("checkpoint"));
			if (!checkpointPerm || !isCheckpointFetched) return "/welcome";

			if (isCheckpointFetched) {
				if (!checkpoints.length) return "/welcome";
				// find any checkpoint permission : "checkpoint-checkpointId"
				// console.log({ checkpointPerm });
				let [, checkpointId] = checkpointPerm.split("-");
				const userCheckpoint = checkpoints.find((_) => _._id == checkpointId);
				if (!userCheckpoint) return "/welcome";

				const { checkpointType, checkpointNumber } = userCheckpoint;

				// valid checkpoint and user has permission -> show the checkpoint page
				return `/checkpoints/${checkpointType}/${checkpointNumber}`;
			}
		}
	}
}
function useRoles(filter) {
	const [getRoles, { data, isLoading, reset }] = useGetRolesMutation({ fixedCacheKey: "getRoles" });

	useEffect(() => {
		if (filter) getRoles(filter);
	}, []);

	const { data: roles, count } = data || { data: [] };
	const roleOptions = [...(roles?.map((_) => ({ label: _.displayName, value: _.role, data: _ })) || [])];

	return { roleOptions, roles, getRoles, isLoading, count, clearRole: reset };
}

function useRolesForFilter(filter) {
	const [getRolesForFilter, { data, isLoading, reset }] = useGetRolesForFilterMutation({ fixedCacheKey: "getRolesForFilter" });

	useEffect(() => {
		if (filter) getRolesForFilter(filter);
	}, []);

	const { data: rolesForFilter, countForFilter } = data || { data: [] };
	const roleFilterOption = [...(rolesForFilter?.map((_)=>({label:_.role, value:_.role})) || [])];

	return { roleFilterOption, rolesForFilter, getRolesForFilter, countForFilter };
}

let isToashShown = false;
function useUserDetail() {
	const { data, isSuccess, isFetching, refetch } = useGetCurrentUserDetailQuery({});
	const { data: userDetail, statusCode } = data || {};
	useEffect(() => {
		const pendingToken = sessionStorage.getItem("secretkey");
		if (isSuccess && userDetail?.privacyPolicyAcknowledged && pendingToken) {
			localStorage.setItem("secretkey", pendingToken);
			sessionStorage.removeItem("secretkey");
		}
	}, [isSuccess, userDetail?.privacyPolicyAcknowledged]);
	useEffect(()=>{
		if(isSuccess && !userDetail && !isToashShown){
			localStorage.removeItem("secretkey");
			sessionStorage.removeItem("secretkey");
			isToashShown = true;
			toastMessage(false, "Failed to fetch user details. Please try to login after few minutes.")
			setTimeout(()=>{
				isToashShown = false;
			},1000)
		}
	},[isSuccess, userDetail])
	return { userDetail, statusCode, isUserDetailSuccess: isSuccess, isFetching, refetch, isSuperAdmin: userDetail?.userType == "superAdmin" };
}

//vms
function useVehicles(filter = {}) {
	const { data, refetch, isLoading, isFetching } = useGetVehiclesQuery(filter, {
		refetchOnMountOrArgChange: true,
	});

	let { data: vehicles, count, message, error } = data || { data: [] };
	if (!vehicles) vehicles = [];
	const vehicleOptions = vehicles.map((_) => ({ label: _.truck_no, value: _._id, data: _ }));
	return { vehicles, count, message, isLoading: isFetching, refetch, vehicleOptions };
}
function useSuppliers(filter) {
	const [getSuppliers, { data, isLoading }] = useGetSuppliersMutation({ fixedCacheKey: "getSuppliers" });
	useEffect(() => {
		if (filter) getSuppliers(filter);
	}, []);

	let { data: suppliers, count, message } = data || { data: [] };
	if (!suppliers) suppliers = [];
	const supplierOptions = suppliers.map((_) => ({ label: _.supplierName, value: _._id }));
	return { suppliers, count, message, isLoading, getSuppliers, supplierOptions };
}

function useProducts(filter) {
	const [getProducts, { data, isLoading }] = useGetProductsMutation({ fixedCacheKey: "getProducts" });
	useEffect(() => {
		if (filter) getProducts(filter);
	}, []);
	let { data: products, count, message, error } = data || { data: [] };
	if (!products) products = [];
	const productOptions = products.map((_) => ({ label: _.productName, value: _._id }));

	return { products, count, message, isLoading, getProducts, productOptions };
}
function useProductCategories(filter) {
	const [getProdCats, { data, isLoading }] = useGetProductCategoriesMutation({ fixedCacheKey: "getProdCats" });
	useEffect(() => {
		if (filter) getProdCats(filter);
	}, []);

	let { data: productCategories, count, message, error } = data || { data: [] };
	if (!productCategories) productCategories = [];
	const options = useMemo(
		() => [
			{ label: "--select--", value: "" },
			...(productCategories?.map((_) => ({
				label: _.productCategory,
				value: _._id,
			})) || []),
		],
		[productCategories]
	);

	return { productCategories, count, message, isLoading, options, getProdCats };
}
function useDynamicModules(filter, fixedCacheKey = "dynamicModule") {
	const [fetchData, { data, isLoading }] = useGetDynamicModuleMutation({ fixedCacheKey });
	useEffect(() => {
		if (filter) fetchData(filter);
	}, []);

	let { data: dynamicModules, count, message } = data || { data: [] };
	if (!dynamicModules) dynamicModules = [];
	return { dynamicModules, count, message, isLoading, getDynamicModules: fetchData };
}
function useDynamicModuleEntries(filter) {
	const [fetchData, { data, isLoading }] = useGetDynamicModuleEntryMutation({ fixedCacheKey: "dynamicModuleEntry" });
	useEffect(() => {
		if (filter) fetchData(filter);
	}, []);

	let { data: dynamicModuleEntries, count, message, tableHeader = [] } = data || { data: [], tableHeader: [] };
	if (!dynamicModuleEntries) dynamicModuleEntries = [];
	return { dynamicModuleEntries, count, message, isLoading, getDynamicModuleEntries: fetchData, tableHeader };
}
//https://redux-toolkit.js.org/rtk-query/usage/cache-behavior#encouraging-re-fetching-with-refetchonmountorargchange
function useCheckpointsOld(filter = {}) {
	const { data, refetch, isLoading, isFetching, isSuccess } = useGetCheckPointQuery(filter, {
		refetchOnMountOrArgChange: 5,
	});
	let { data: checkpoints, count, message, error } = data || { data: [] };
	if (!checkpoints) checkpoints = [];
	checkpoints = [...checkpoints].sort((a, b) => a.checkpointNumber - b.checkpointNumber);

	const checkpointOptions = checkpoints.map((_) => ({ label: _.displayName, value: _._id }));
	return { checkpoints, refetch, isLoading: isFetching, checkpointOptions, isSuccess: error == false, isCheckpointFetched: isSuccess };
}
function useCheckpoints(filter, fixedCacheKey = "checkpoints", includePlantInLabel = false) {
	const [getCheckpoints, { data, isLoading, isFetching, isSuccess, reset }] = useGetCheckPointOnCallMutation({ fixedCacheKey });

	let { checkpoints, count, error } = useMemo(() => {
		let { data: checkpoints, count, error } = data || { data: [] };
		if (!checkpoints) checkpoints = [];
		return { checkpoints, count, error };
	}, [data]);
	const checkpointOptions = checkpoints
		.filter((item) => item.hasOwnProperty("plantId"))
		.map((_) => ({ label: includePlantInLabel ? `${_.displayName}(${_.plantName})` : _.displayName, value: _._id, checkpointNumber: _.checkpointNumber })); // donot remove checkpointNumber

	useEffect(() => {
		if (filter) getCheckpoints(filter);
	}, []);

	let checkoutConfigMap = checkpoints.reduce((res, checkpoint) => {
		res[checkpoint._id] = checkpoint.checkoutConfig;
		return res;
	}, {});
	return { checkpoints, checkpointOptions, getCheckpoints, clearCheckpointData: () => reset(), count, isCheckpointFetched: isSuccess, isLoading, isSuccess: error == false, checkoutConfigMap };
}

function useTemplateDetail(filter, fixedCacheKey = "checkpointForm") {
	const [getTemplateDetail, { data, isLoading, isSuccess }] = useGetTemplateDetailOnCallMutation({ fixedCacheKey });
	useEffect(() => {
		if (filter) getTemplateDetail(filter);
	}, []);
	let { data: templateDetail, count, message, error } = data || { data: { attributes: [] } };
	return { templateDetail, isLoading, isSuccess, error, getTemplateDetail };
}
function useTemplateList(filter) {
	const [fetchData, { data, isLoading }] = useGetTemplatesMutation({ fixedCacheKey: "templateList" });
	useEffect(() => {
		if (filter) fetchData(filter);
	}, []);

	let { data: templates, count, message, error } = data || { data: [] };
	if (!templates) templates = [];
	const templateOptions = [
		...(templates?.map((_) => {
			return { label: _.name, value: _._id };
		}) || []),
	];
	return { templates, count, message, isLoading, getTemplateList: fetchData, templateOptions };
}
function useCheckpointData(filter = {}) {
	const [trigger, { data, refetch, isLoading, isFetching, isSuccess }] = useLazyGetCheckpointDataListQuery(filter, {
		refetchOnMountOrArgChange: true,
	});
	let { data: checkpointData, count, message, error, tableHeader = [] } = data || { data: [], tableHeader: [] };
	return { trigger, checkpointData, tableHeader, isLoading: isFetching, isSuccess, refetch, count, message, error };
}
// JUST MADE THIS MUTATION ONLY FOR DOWNLOADING THE EXCEL REPORT ON THE ALL TRIP SO THAT TO NOT MAKE API CALL TWICE
function useCheckpointDataForExcelDownload(filter = {}) {
  const [getCheckpointDataListForExcelDownload, { data, isLoading }] = useGetCheckpointDataListForExcelDownloadMutation();
  const { data: checkpointDataForDownload = [],count, message,error, } = data || {};
  return {getCheckpointDataListForExcelDownload, checkpointDataForDownload, isLoading, count,  message, error, };
}

function useTripDetails(filter) {
	const [getTripDetail, { data, isLoading }] = useGetTripDetailMutation({});
	useEffect(() => {
		if (filter) getTripDetail(filter);
	}, []);

	let { data: tripDetail, count, message, error } = data || { data: [] };
	if (!tripDetail) tripDetail = [];
	return { tripDetail, count, message, isLoading, getTripDetail, error };
}

function useRule(filter) {
	const [getRuleDetails, { data, isLoading }] = useGetRuleDetailMutation({});
	useEffect(() => {
		if (filter) getRuleDetails(filter);
	}, []);

	let { data: rules, count, message } = data || { data: [] };
	if (!rules) rules = [];
	return { rules, count, message, isLoading, getRuleDetails };
}
function useThirdPartyLogs(filter) {
	const [getThirdPartyLogs, { data, isLoading }] = useGetThirdPartyLogsMutation({ fixedCacheKey: "apilogs" });
	useEffect(() => {
		if (filter) getThirdPartyLogs(filter);
	}, []);

	let { data: logs, count, message, tableHeader } = data || { data: [] };
	if (!logs) logs = [];
	return { logs, count, message, tableHeader, isLoading, getThirdPartyLogs };
}
// util
function useClickOutside(ref, callback) {
	const handleClick = (e) => {
		if (ref.current && !ref.current.contains(e.target)) {
			callback();
		}
	};
	useEffect(() => {
		document.addEventListener("click", handleClick);
		return () => document.removeEventListener("click", handleClick);
	});
}

function useSocketCheckpointForDashboard() {
    const { renderedMode } = useBranding();
	const [checkpointEvents, setCheckpointEvents] = useState([]);
	useEffect(() => {
		function onCheckpointEvent(eventMsg) {
			const { type, data } = eventMsg;
			if (type === "new_notification") {
				// console.log("notig", data);
				const updatedData = data.map((ele) => {
					const theme = renderedMode;
					return { ...ele, outTime: null, bgColor: theme === "darkMode" ? "#3d533d" : "#e6ffe6" };
				});
				setCheckpointEvents((old) => [...old, ...updatedData]);
			}
		}
		socket.on("new_notification", onCheckpointEvent);

		return () => {
			socket.off("new_notification", onCheckpointEvent);
		};
	}, [renderedMode]);
	return { checkpointEvents };
}

function useRoomForDevice({ plantIds: propPlantIds, preferPlantsFromProps = false } = {}) {
    const { plantId } = usePlantContext();
    const { userDetail } = useUserDetail();
    const [status, setStatus] = useState('idle'); // idle | subscribing | subscribed | error
    const [subscribedPlants, setSubscribedPlants] = useState([]);
    const [error, setError] = useState(null);

    const plantIds = preferPlantsFromProps ? propPlantIds : (plantId ? [plantId] : []);

    useEffect(() => {
        if (!plantIds?.length) {
            setStatus('idle');
            setSubscribedPlants([]);
            return;
        }

        const subscribeData = {
            type: "PlantRoom",
            plantIds,
            userId: userDetail?.email,
            orgId: userDetail?.orgId,
            userRoleId: userDetail?.userRoleId,
            userType: userDetail?.userType,
            deviceId: sessionStorage.getItem("deviceId") || guidGenerator(),
        };

        function subscribe() {
            setStatus('subscribing');
            setError(null);
            socket.emit("subscribe", subscribeData);
        }

        socket.on("connect", subscribe);
        if (socket.connected) subscribe();

        return () => {
            socket.off("connect", subscribe);
            socket.emit("unsubscribe", { plantIds });
        };
    }, [JSON.stringify(plantIds)]);

    return { status, subscribedPlants, error };
}

function useSocket() {
	const { setSocketData } = useSocketContext();
	const { setDeviceData } = useDeviceContext();
	const { updatePlantId } = usePlantContext();

	const navigate = useNavigate();
	const { userDetail } = useUserDetail();

	const { renderedMode } = useBranding();
    const modeRef = useRef(renderedMode);
    modeRef.current = renderedMode;
	if (!sessionStorage.getItem("deviceId")) {
		sessionStorage.setItem("deviceId", guidGenerator());
	}

	const [isConnected, setIsConnected] = useState(socket.connected);

	const toastId = useRef(null);

	function onNewNotification(eventMsg) {
		let { type, data } = eventMsg;
		// console.log(type, data, " ---> EVENT FIRED ");
		switch (type) {
			case "vehicle-entering-alert":
				onNewVehicleAlert(data);
				break;
			case "excel-report-download":
				onDownloadExcel(data);
				break;
			case "new_notification":
				const updatedData = data.map((ele) => {
					return { ...ele, outTime: null, bgColor: modeRef.current === "darkMode" ? "#3d533d" : "#e6ffe6" };
				});
				setSocketData((o) => ({ ...o, checkpointEvents: [...o.checkpointEvents, ...updatedData] }));
				break;
			case "range-checkout":
				let oldEventExists = false;
				setSocketData((old) => {
					let fauxOld = JSON.parse(JSON.stringify(old.checkpointEvents));
					fauxOld = fauxOld.map((trip) => {
						if (trip._id == data[0]?._id && trip.current_checkpoint_no == data[0]?.current_checkpoint_no) {
							oldEventExists = true;
							return {
								...trip,
								...data[0],
								SOCKET_EVENT_TYPE: type,
								bgColor: modeRef.current === "darkMode" ? "#3d533d" : "#e6ffe6",
							};
						}
						return trip;
					});
					return {
						...old,
						checkpointEvents: fauxOld,
					};
				});
				if (!oldEventExists) {
					setSocketData((old) => {
						return {
							...old,
							checkpointEvents: [
								...old.checkpointEvents,
								...data.map((ele) => {
									return { ...ele, SOCKET_EVENT_TYPE: type, bgColor: modeRef.current === "darkMode" ? "#3d533d" : "#e6ffe6" };
								}),
							],
						};
					});
				}
				break;
			case "tag_mapping":
				setSocketData((o) => ({ ...o, tagData: data }));
				break;
			case "device-status":
				if (Array.isArray(data)) {
					setDeviceData((o) => ({
						...o,
						...data?.reduce((res, curr) => {
							res[curr.deviceId] = {
								deviceStatus: curr.deviceStatus,
								state: curr.state,
								info: curr.info,
							};
							return res;
						}, {}),
					}));
				}
				break;
			case "asset-tag-scan":
				setSocketData((o) => {
					let assetScanData = [...o.assetScanData];
					if (!assetScanData.find((_) => _.epc == data.epc)) {
						assetScanData = [{ ...data, type: "scanned" }, ...assetScanData];
					}
					return {
						...o,
						assetScanData,
					};
				});
				break;
			case "get_approval":
				let _updatedData = { ...data, outTime: null, bgColor: modeRef.current === "darkMode" ? "#3d533d" : "#e6ffe6" };
				setSocketData((o) => ({ ...o, approvalEvents: [_updatedData, ...o.approvalEvents] }));
				break;
			case "rule_violation":
				onRuleViolation(data);
				break;
            case "notify-for-entry":
				console.log("[ notify-for-entry ] Received Entry Intimation.", eventMsg);
				break;
			default:
				console.error(`Unhandled Socket Event Type : ${type}`);
		}
	}

	function onRuleViolation(data) {
		// console.log("Rule Violation --> ", data);
		const { message, data: alertData } = data;
		const go = (data) => {
			// console.log("clicked");
			if (data) {
				// navigate("/manage-trip", { state: { data } });
			}
		};
		const msg = (
			<div style={{ cursor: "pointer" }} onClick={() => go()}>
				{message}
			</div>
		);
		toastMessage(false, msg, {
			position: "top-right",
			autoClose: 5000, // Closes after 5s
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
		});
	}

	function onNewVehicleAlert(data) {
		// console.log("Socket event --> ", data);
		if (data[0] && data[0].next_checkpoint?.length) {
			let chkpnts = "";
			data[0].next_checkpoint.forEach((pnt) => (chkpnts += ` ${pnt.displayName} /`));
			chkpnts = chkpnts.slice(0, chkpnts.length - 1);
			const go = (data) => {
				// console.log("clicked");
				if (data) {
					if (updatePlantId({ value: data.plantId }) === false) return;
					navigate("/manage-trip", { state: { data } });
				}
			};
			const msg = (
				<div style={{ cursor: "pointer" }} onClick={() => go(data[0].data)}>
					{`Vehicle ${data[0]?.data?.vehicleNumber || ""} arriving at - ${chkpnts}`}
				</div>
			);
			toastMessage(true, msg);
		} else {
			// console.log("Empty data in socket event");
		}
	}

	function onDownloadExcel(data) {
		// console.log(toastId.current)
		if (data.type === "excel-downloading") {
			toastId.current = toastMessage("loading", "Your report is in progress will download soon..", { position: "bottom-right", autoClose: 300000, closeOnClick: true });
			return;
		} else if (data.type === "excel-error") {
			dismissToast(toastId.current);
			return;
		} else {
			// console.log("received excel data --> ", data)
			dismissToast(toastId.current);
			saveAsExcel(data.data, "report");
			toastMessage(true, "Report Downloaded", { position: "bottom-right" });
		}
	}

	useEffect(() => {
		if (!userDetail) return;
		// if (socket.connected) return;
		// console.log("CALLED");
		// console.log("socket mount", socket.connected);

		if (socket.connected) {
			emitLogin();
		}

		function onConnect() {
			setIsConnected(true);
			// console.log("socket connected");
			emitLogin();
		}

		function onDisconnect() {
			setIsConnected(false);
			// console.log("socket disconnected");
		}

		function emitLogin() {
			const data = {
				userId: userDetail?.email,
				orgId: userDetail?.orgId,
				userRoleId: userDetail?.userRoleId,
				userType: userDetail?.userType,
				deviceId: sessionStorage.getItem("deviceId") || guidGenerator(),
			};
			// console.log("Login emit", data);
			socket.emit("user_login", data);
		}

        function onRoomInfo(data){
            console.log(" -- Room Socket Recieved -- ", data)
        }

		socket.on("connect", onConnect);
		socket.on("disconnect", onDisconnect);
        socket.on("subscribe:info", onRoomInfo);

		socket.on("new_notification", onNewNotification);

		return () => {
			// console.log("socket unmount");
            socket.off("subscribe:info", onRoomInfo);
			socket.off("connect", onConnect);
			socket.off("disconnect", onDisconnect);
			socket.off("new_notification", onNewNotification);
		};
	}, [userDetail]);
}

// Applies the resolved plant-theme branding (plant override -> organization default -> application
// default, see context/brandingContext.jsx) to the document: CSS custom properties, favicon, title.
// Personal light/dark selection (`renderedMode`) overrides the configured mode for rendering only.
function useApplytheme() {
	const { effective, renderedMode } = useBranding();
	const primaryColor = effective.theme.primaryColor.value;

	useEffect(() => { themeMode(renderedMode, primaryColor); }, [renderedMode, primaryColor]);
	useEffect(() => () => { setFavicon("/favicon.ico"); themeMode("lightMode", "#ff7200"); }, []);

	useEffect(() => {
		if (effective.favIcon.hidden) {
			// An explicit hide must never let the browser fall back to fetching /favicon.ico.
			setFavicon("data:,");
		} else {
			setFavicon(effective.favIcon.value?.url || "/favicon.ico");
		}
	}, [effective.favIcon.hidden, effective.favIcon.value?.url]);

	useEffect(() => {
		setTitle(effective.title.value || "LynkID");
		return () => {
			setTitle("LynkID");
		};
	}, [effective.title.value]);

	return { logo: effective.logo.hidden ? null : effective.logo.value?.url };
}

function hexToRgba(hex, opacity) {
	// Remove the leading '#' if it's there
	hex = hex.replace(/^#/, "");

	// Parse the r, g, b values
	let r = parseInt(hex.substring(0, 2), 16);
	let g = parseInt(hex.substring(2, 4), 16);
	let b = parseInt(hex.substring(4, 6), 16);

	// Return the RGBA color
	return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export const themeMode = (selectedTheme, primaryColor) => {
	if (selectedTheme == "darkMode") {
		document.documentElement.style.setProperty("--set-primary-color", primaryColor);
		/* default theme */
		document.documentElement.style.setProperty("--set-navbar-bg-color", "#262626");
		document.documentElement.style.setProperty("--set-background-color", "#232323");
		document.documentElement.style.setProperty("--set-text-color", "#e3e3e3");
		document.documentElement.style.setProperty("--set-button-disabled-text-color", "#575757");
		/* input-field */
		document.documentElement.style.setProperty("--set-input-border-color", "#505050");
		document.documentElement.style.setProperty("--set-input-placeholder-color", "#ababab");
		document.documentElement.style.setProperty("--set-generic-surface-bg-color", "#333333");
		document.documentElement.style.setProperty("--set-native-control-color-scheme", "dark");
		/* table */
		document.documentElement.style.setProperty("--set-table-inner-header-bg-color", hexToRgba(primaryColor, 0.18));
		document.documentElement.style.setProperty("--set-table-header-bg-color", "#323232");
		document.documentElement.style.setProperty("--set-table-header-text-color", "#e3e3e3");
		document.documentElement.style.setProperty("--set-table-body-bg-color", "#2d2d2d");
		document.documentElement.style.setProperty("--set-table-pagination-bg", "#323232");

		/* page  */
		document.documentElement.style.setProperty("--set-heading-text-color", "#ebebeb");
		document.documentElement.style.setProperty("--set-heading-text-bold", "500");
		document.documentElement.style.setProperty("--set-heading-text-color", "#ebebeb");
		/* sidebar */
		document.documentElement.style.setProperty("--set-sidebar-bg-color", "#ffffff");
		document.documentElement.style.setProperty("--set-sidebar-hover-bg-color", "#72737794");
		document.documentElement.style.setProperty("--set-icon-color", "#c8c8c8");

		//    /* outlinebtn-button */
		document.documentElement.style.setProperty("--set-lynkitBlackEmpty-color", "var(--gray-color)");
		document.documentElement.style.setProperty("--set-lynkitBlackEmpty-Hvr-color", "var(--gray-color)");
		document.documentElement.style.setProperty("--set-outlinebtn-border", "2px");
		document.documentElement.style.setProperty("--set-lynkitBlackEmpty-border-color", "var(--dark-gray-color)");
		document.documentElement.style.setProperty("--set-lynkitBlackEmpty-border-Hvr-color", "var(--dark-gray-color)");
		document.documentElement.style.setProperty("--set-lynkitBlackEmpty-border-Hvr-bg-Color", "var(--dark-gray-color)");
		document.documentElement.style.setProperty("--set-lynkitBlackFill-bg-color", "var(--dark-gray-color)");
		document.documentElement.style.setProperty("--set-lynkitBlackFill-bg-Hvr-color", "var(--dark-gray-color)");
		/* dropdown */
		document.documentElement.style.setProperty("--set-option-hover-color", "#404040");
		//    /* modal */
		document.documentElement.style.setProperty("--set-model-bg-color", "#383838");
	}
	if (selectedTheme == "lightMode") {
		document.documentElement.style.setProperty("--set-primary-color", primaryColor);
		document.documentElement.style.setProperty("--set-navbar-bg-color", "#ffffff");
		document.documentElement.style.setProperty("--set-background-color", "#ffffff");
		document.documentElement.style.setProperty("--set-text-color", "#000000");
		document.documentElement.style.setProperty("--set-button-disabled-text-color", "#6b6b6b");
		/* input-field */
		document.documentElement.style.setProperty("--set-input-border-color", "#c2c7cc");
		document.documentElement.style.setProperty("--set-input-placeholder-color", "#000000");
		document.documentElement.style.setProperty("--set-generic-surface-bg-color", "#ffffff");
		document.documentElement.style.setProperty("--set-native-control-color-scheme", "light");
		/* table */
		document.documentElement.style.setProperty("--set-table-inner-header-bg-color", hexToRgba(primaryColor, 0.2));
		document.documentElement.style.setProperty("--set-table-header-bg-color", "#D9D9D9");
		document.documentElement.style.setProperty("--set-table-header-text-color", "rgb(69, 90, 100)");
		document.documentElement.style.setProperty("--set-table-body-bg-color", "#ffffff");
		document.documentElement.style.setProperty("--set-table-pagination-bg", "#ffffff");

		/* page  */
		document.documentElement.style.setProperty("--set-heading-text-color", "#000000");
		document.documentElement.style.setProperty("--set-heading-text-bold", "bold");
		document.documentElement.style.setProperty("--set-heading-text-color", "#000000");
		/* sidebar */
		document.documentElement.style.setProperty("--set-sidebar-bg-color", "#ffffff");
		document.documentElement.style.setProperty("--set-sidebar-hover-bg-color", "rgba(0, 0, 0, 0.04)");
		document.documentElement.style.setProperty("--set-icon-color", "rgba(0, 0, 0, 0.54)");

		//    /* outlinebtn-button */
		document.documentElement.style.setProperty("--set-lynkitBlackEmpty-color", "#000000");
		document.documentElement.style.setProperty("--set-lynkitBlackEmpty-Hvr-color", "#ffffff");
		document.documentElement.style.setProperty("--set-outlinebtn-border", "1px");
		document.documentElement.style.setProperty("--set-lynkitBlackEmpty-border-color", "#000000");
		document.documentElement.style.setProperty("--set-lynkitBlackEmpty-border-Hvr-color", "#000000");
		document.documentElement.style.setProperty("--set-lynkitBlackEmpty-border-Hvr-bg-Color", "#000000");
		document.documentElement.style.setProperty("--set-lynkitBlackFill-bg-color", "#000000");
		document.documentElement.style.setProperty("--set-lynkitBlackFill-bg-Hvr-color", "#000000");

		/* dropdown */
		document.documentElement.style.setProperty("--set-option-hover-color", "#d0d1d3");
		//    /* modal */
		document.documentElement.style.setProperty("--set-model-bg-color", "#ffffff");
	}
};

function useWindowOpener() {
	let location = useLocation();
	const navigate = useNavigate();
	let searchParams = new URLSearchParams(location.search);
	let authParam = searchParams.get("auth");
	let actionParam = searchParams.get("action");
	let showSidebar = searchParams.get("sidebar") == "true";
	let plantIdParam = searchParams.get("plantId");

	if (!localStorage.getItem("url")) {
		localStorage.setItem("url", window.location.href);
	}

	// console.log({showSidebar})
	// console.log({
	// 	type: window.opener,
	// 	authParam,
	// 	actionParam
	// })
	let windowType;
	if (authParam) {
		localStorage.removeItem("secretkey");

		if (window.opener) {
			windowType = "windowOpener";
			window.opener.postMessage({ perkey: "sendphrase" }, "*");
		} else {
			windowType = "windowIframe";
			window.parent.postMessage(JSON.stringify({ perkey: "sendphrase" }), "*");
			// Set an item in the localStorage with the key "showFrame" and the value 'true'
		}
	}
	window.addEventListener("message", (evt) => {
		if (evt.data.baseURL) {
			localStorage.setItem("baseURL", evt.data.baseURL);
		}
		if (evt.data.phrase) {
			if (["lynktrac", "LYNKID", "WMS"].includes(authParam)) {
				localStorage.setItem("windowType", windowType);
				localStorage.setItem("project", authParam);
				localStorage.setItem("activeURL", actionParam);
				localStorage.setItem("secretkey", evt.data.phrase);
				localStorage.setItem("showSidebar", showSidebar);
				localStorage.setItem("baseURL", evt.data.baseURL);
				// plantId passed by the iframe host; PlantProvider picks it up from localStorage
				const iframePlantId = evt.data.plantId || plantIdParam;
				if (iframePlantId) localStorage.setItem("plantId", iframePlantId);
				return navigate(actionParam);
			}
		}
	});
}

export function useGraphTimelineData(filter) {
	const [getGraphTimelineData, { data, isLoading }] = useGetGraphTimelineDataMutation({});
	useEffect(() => {
		if (filter) getGraphTimelineData(filter);
	}, []);
	let { data: graphTimelineData, count, message } = data || { data: [] };
	if (!graphTimelineData) graphTimelineData = [];
	return { graphTimelineData, count, message, isLoading, getGraphTimelineData };
}

function useCheckInternet() {
	const [isOnline, setOnlineStatus] = useState(navigator.onLine);
	const navigate = useNavigate();

	useEffect(() => {
		const handleOffline = () => {
			// console.log("offline=========>", localStorage.getItem("offlinePermission"));
			if (localStorage.getItem("offlinePermission") == "true") {
				setOnlineStatus(false);
				navigate("/offline");
			}
		};

		const handleOnline = () => {
			// console.log("online=========>");
			setOnlineStatus(true);
			// Optionally, you can navigate back to the previous page when online
			// window.history.back();
		};

		window.addEventListener("offline", handleOffline);
		window.addEventListener("online", handleOnline);

		// Cleanup function to remove event listeners when component unmounts
		return () => {
			window.removeEventListener("offline", handleOffline);
			window.removeEventListener("online", handleOnline);
		};
	}, [navigate]);

	return isOnline;
}

function useOfflineTemplate() {
	const { data, isLoading, isFetching, isSuccess } = useGetTemplateDetailQuery(
		{ type: "offline", template_name: "Offline template", organization_id: "all" },
		{ refetchOnMountOrArgChange: false } // Set to false to prevent automatic refetch
	);
	// type : 'offline', template_name: 'Offline template', organization_id : "all"
	useEffect(() => {
		if (isSuccess) {
			const templateDetail = data?.data?.attributes || [];
			let updatedTemplateDetail = JSON.parse(JSON.stringify(templateDetail));

			// Array to store promises for each API request
			const promises = templateDetail.map((attribute, index) => {
				// console.log('field===>', attribute.label);
				// Your fetchSourceDropdownData logic goes here
				function fetchSourceDropdownData(field, inputValue = "") {
					return new Promise((resolve, reject) => {
						const { _id, api, apiMethod, apiParameters, apiBindingKey, apiBindingValue, apiDataReturnKey = "data" } = field;

						let payload;
						if (apiMethod.toLowerCase() == "get") {
							payload = apiParameters?.map((_) => `${encodeURIComponent(_.label)}=${encodeURIComponent(_.value)}`).join("&");
						} else {
							payload = {};
							apiParameters?.forEach((_) => {
								payload[_.label] = _.value == "onSearch" ? inputValue : _.value;
							});
						}

						// console.log("Fetching dropdown options from API...",api, payload);
						let authInfo = { auth: field.auth, authMethod: field.authMethod, authParams: field.authParams };
						customAPI(payload, api, apiMethod, null, authInfo)
							.then(({ data }) => {
								if (!data.error) {
									let _data = data[apiDataReturnKey] || [];

									if (updatedTemplateDetail[index]._id == _id) {
										updatedTemplateDetail[index]["options"] = _data?.map((el) => ({ label: el[apiBindingKey || "label"], value: el[apiBindingValue || "value"] }));
									}
									resolve(); // Resolve the promise when API request is successful
								} else {
									reject(data.error); // Reject the promise if there's an error
								}
							})
							.catch((error) => {
								reject(error); // Catch and reject any errors that occur during the API request
							});
					});
				}

				return fetchSourceDropdownData(attribute);
			});

			// Wait for all promises to resolve
			Promise.all(promises)
				.then(() => {
					// console.log('updatedTemplateDetail==>', updatedTemplateDetail);
					localStorage.setItem("templateDetail", JSON.stringify(updatedTemplateDetail));
				})
				.catch((error) => {
					console.error("An error occurred while fetching data:", error);
				});
		}
	}, [data, isSuccess]);

	// You may return any necessary data here
}

function useDashboardStats(filter) {
	const [getDashboardStats, { data, isLoading }] = useGetDashboardStatsMutation({ fixedCacheKey: "getDashboardStats" });
	useEffect(() => {
		if (filter) getDashboardStats(filter);
	}, []);

	let { data: stats, count, message, error } = data || { data: [] };
	if (!stats) stats = [];
	return { stats, count, message, isLoading, getDashboardStats };
}

function useCheckpointTypes(filter) {
	const [getCheckpointTypes, { data, isLoading }] = useGetCheckpointTypeMutation({ fixedCacheKey: "getCheckpointTypes" });
	useEffect(() => {
		if (filter) getCheckpointTypes(filter);
	}, []);

	let { data: checkpointTypes, count, message } = data || { data: [] };
	if (!checkpointTypes) checkpointTypes = [];
	const checkpointTypeOptions = checkpointTypes.map((_) => ({ label: _.checkpointType, value: _.checkpointType }));

	return { checkpointTypes, count, message, isLoading, getCheckpointTypes, checkpointTypeOptions };
}

function usePlant(filter, fixedCacheKey = "plants", includeSelectAll = false) {
	const [getPlantDetails, { data, isLoading, reset }] = useGetPlantMutation({ fixedCacheKey });
	useEffect(() => {
		if (filter) getPlantDetails(filter);
	}, []);
	let { data: plants, count, message } = data || { data: [] };
	if (!plants) plants = [];
	const plantOptions = useMemo(() => {
		let opts = plants.map((_) => ({ label: _.plantName, value: _._id, data: _ }));
		return includeSelectAll ? [{ label: "Select All", value: "all" }, ...opts] : opts;
	}, [plants]);
	return { plants, count, message, isLoading, getPlantDetails, plantOptions, clearPlants: reset };
}

function useResource(filter, fixedCacheKey = "resource") {
	const [getResourceDetails, { data, isLoading, reset }] = useGetResourceMutation({ fixedCacheKey });
	useEffect(() => {
		if (filter) getResourceDetails(filter);
	}, []);
	let { data: resources, count, message } = data || { data: [] };
	if (!resources) resources = [];
	return { resources, count, message, isLoading, getResourceDetails };
}
function useStateOffices(filter, fixedCacheKey = "states") {
	const [getStateOffices, { data, isLoading, reset }] = useGetStateOfficesMutation({ fixedCacheKey });
	useEffect(() => {
		if (filter) getStateOffices(filter);
	}, []);
	let { data: states, count, message } = data || { data: [] };
	if (!states) states = [];
	const statesOptions = useMemo(() => states.map((_) => ({ label: _.state, value: _.state })), [states]);
	return { states, count, message, isLoading, getStateOffices, statesOptions };
}

export {
	useDevices,
	useTags,
	useUsers,
	useUserDetail,
	useOrgs,
	useWorkflow,
	useVehicles,
	useSuppliers,
	useProducts,
	useProductCategories,
	useCheckpoints,
	useClickOutside,
	useTemplateDetail,
	useCheckpointData,
	useTripDetails,
	useRule,
	useRoles,
	useRolesForFilter,
	useUserPermissions,
	useAuth,
	useSocket,
	useApplytheme,
	useSocketCheckpointForDashboard,
	useDashboardStats,
	useWindowOpener,
	useCheckInternet,
	useOfflineTemplate,
	useCheckpointTypes,
	useShowReload,
	useTagsForScan,
	useFocus,
	useAssetTags,
	useDynamicModules,
	useTemplateList,
	useDynamicModuleEntries,
	useDeviceLogs,
	useAllPermissionSet,
	useTcpClients,
	useThirdPartyLogs,
	usePlant,
	useStateOffices,
	useResource,
	useTelemetryData,
	useCheckpointDataForExcelDownload,
    useRoomForDevice
};

function guidGenerator() {
	var S4 = function () {
		return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
	};
	return S4() + S4() + "-" + S4() + "-" + S4() + "-" + S4() + "-" + S4() + S4() + S4();
}

function useServiceWorker() {
	const [waitingWorker, setWaitingWorker] = useState(null);
	const [showReload, setShowReload] = useState(false);
	// called when a service worker
	// updates. this function is a callback
	// to the actual service worker
	// registration onUpdate.
	const onSWUpdate = useCallback((registration) => {
		// console.log("on sw update")
		setShowReload(true);
		setWaitingWorker(registration.waiting);
	}, []);
	// simply put, this tells the service
	// worker to skip the waiting phase and then reloads the page
	const reloadPage = useCallback(() => {
		waitingWorker?.postMessage({ type: "SKIP_WAITING" });
		setShowReload(false);
		window.location.reload();
	}, [waitingWorker]);
	// register the service worker
	useEffect(() => {
		// If you want your app to work offline and load faster, you can change
		// unregister() to register() below. Note this comes with some pitfalls.
		// Learn more about service workers: https://cra.link/PWA
		serviceWorkerRegistration.register({
			onUpdate: onSWUpdate,
		});
	}, [onSWUpdate]);

	// useEffect(() => {
	// 	serviceWorkerRegistration.unregister();
	// }, []);
	return { showReload, waitingWorker, reloadPage };
}

function useShowReload() {
	const { showReload, waitingWorker, reloadPage } = useServiceWorker();

	useEffect(() => {
		if (showReload && waitingWorker) {
			setTimeout(() => {
				toast.info("New Version Available! Click to reload.", {
					position: "top-right",
					autoClose: false,
					hideProgressBar: false,
					closeOnClick: false,
					pauseOnHover: false,
					draggable: false,
					progress: undefined,
					theme: "colored",
					onClick: () => {
						reloadPage();
					},
					onClose: () => {
						reloadPage();
					},
				});
			}, 1000);
		}
	}, [showReload, waitingWorker, reloadPage]);
}

function useFocus(ref) {
	const [isFocus, setIsFocus] = useState(false);
	useEffect(() => {
		if (!isFocus || !ref.current) return;

		const focusListener = (e) => {
			// console.log("OUT OF FOCUS");
			setIsFocus(false);
		};
		const focusInListener = (e) => {
			// console.log("FOCUSSS");
			setIsFocus(true);
		};
		ref.current.addEventListener("focusout", focusListener);
		ref.current.addEventListener("focusin", focusInListener);

		return () => {
			// console.log("cleanup");
			ref.current?.removeEventListener("focusout", focusListener);
			ref.current?.removeEventListener("focusin", focusInListener);
		};
	}, [isFocus]);

	return { isFocus, setIsFocus };
}

function useTcpClients(filter = {}) {
	const [getTcpClients, { data, isLoading }] = useGetTcpClientsMutation({ fixedCacheKey: "getTcpClients" });

	useEffect(() => {
		if (filter) {
			getTcpClients(filter);
		}
	}, []);
	let { data: tcpClients = {}, count, message, error } = data || { data: {} };

	let _tcpClients = useMemo(() => tcpClients, [data]);
	return { tcpClients: _tcpClients, count, message, isLoading, getTcpClients };
}

export function useChatbot() {
	const { userDetail } = useUserDetail();
	const [messages, setMessages] = useState([{ text: "Hello! How can I assist you today?", sender: "bot" }]);
	const ws = useRef(null);

	// Connect WebSocket when userDetail is available
	useEffect(() => {
		if (!userDetail) return;

		const metaData = {
			orgName: userDetail.orgName,
			userId: userDetail.userId,
			userEmail: userDetail.email,
		};

		//ws://4.240.79.40:8769/ws
		ws.current = new WebSocket("wss://chat.lynkit.in/ws");
		ws.current.onopen = () => {
			// console.log("Connected to chatbot socket");
			ws.current.send(JSON.stringify(metaData));
		};

		ws.current.onmessage = (ev) => {
			// console.log("Event received", ev);
			if (ev.data == "Connection established. Send metadata first." || ev.data == "Metadata received. Start the conversation!") {
				return;
			}
			setMessages((prev) => {
				let next = prev.slice(0, -1);
				next.push({ text: ev.data, sender: "bot" });
				return next;
			});
		};

		ws.current.onclose = () => console.log("Disconnected from chatbot socket");
		ws.current.onerror = (err) => console.log("ERROR: ", err);

		return () => ws.current.close();
	}, [userDetail]);

	const sendMessage = (message) => {
		if (ws.current && ws.current.readyState === WebSocket.OPEN) {
			setMessages((prev) => [...prev, { text: message, sender: "user" }, { text: "...", sender: "bot" }]);
			ws.current.send(message);
		} else {
			console.error("WebSocket is not connected");
		}
	};

	return { messages, sendMessage };
}

export function useUserTableHeader(filter) {
	const [getUserTableHeaders, { data, isLoading }] = useGetUserTableHeadersMutation({ fixedCacheKey: "getUserTableHeaders" });

	useEffect(() => {
		if (filter) {
			getUserTableHeaders(filter);
		}
	}, []);

	let { data: userTableHeaders = {}, message, error } = data || { data: {} };
	return { userTableHeaders, message, isLoading, getUserTableHeaders };
}

export function useDebounce(cb, wait = 500) {
	const ref = useRef(cb);
	useEffect(() => {
		ref.current = cb;
	}, [cb]);

	const debouncedCb = useMemo(() => {
		const func = () => {
			ref.current?.();
		};
		return debounce(func, wait);
	}, []);

	return debouncedCb;
}
export function useFactoryDashboardData(filter) {
	const [getFactoryDashboardData, { data, isLoading }] = useFactoryDashboardDataMutation();
	useEffect(() => {
		if (filter) getFactoryDashboardData(filter);
	}, []);

	let { data: factoryData, count, message, error } = data || { data: [] };
	if (!factoryData) factoryData = [];
	return { factoryData, count, message, isLoading, getFactoryDashboardData };
}
export function useGetOrgInsights(filter) {
	const [getOrgInsights, { data, isLoading }] = useGetOrgInsightsMutation();
	useEffect(() => {
		if (filter) getOrgInsights(filter);
	}, []);

	let { data: orgInsightsData, count, message, error } = data || { data: [] };
	if (!orgInsightsData) orgInsightsData = [];
	return { orgInsightsData, count, message, isLoading, getOrgInsights };
}
export function useGetPlantComments(filter) {
	const [getPlantComments, { data, isLoading }] = useViewPlantCommentMutation();
	useEffect(() => {
		if (filter) getPlantComments(filter);
	}, []);

	let { data: plantComments, count, message, error } = data || { data: [] };
	if (!plantComments) plantComments = [];
	return { plantComments, count, message, isLoading, getPlantComments };
}

export function useIdleWarehouses(filter) {
	const [getIdleWarehouses, { data, isLoading }] = useGetIdleWarehousesMutation();
	useEffect(() => {
		if (filter) getIdleWarehouses(filter);
	}, []);

	let { data: idleWarehouseData, count, message, error } = data || {};
	return { idleWarehouseData, count, message, error, isLoading, getIdleWarehouses };
}
